import React, { useState, useRef, useEffect } from "react";
import "./commanComponents.css";
import svgData from "../constants/utilities/svgData";
import { useLocation, useNavigate } from "react-router-dom";
import { PaginationContext } from "../constants/context/PaginationProvider";
import { getUserDetails } from "../constants/helpers/getUserDetails";
import { validateInputSecurity } from "../constants/helpers/helperFunctions";
import toast from "react-hot-toast";

export default function Header({ onMenuClick }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { globalSearchString, setGlobalSearchString, customSearchPlaceholder, customSearchTooltip } = React.useContext(PaginationContext);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const profileRef = useRef(null);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    const errorMsg = validateInputSecurity(val);
    if (errorMsg) {
      toast.error(errorMsg);
      return;
    }
    setGlobalSearchString(val);
  };

  React.useEffect(() => {
    setGlobalSearchString("");
  }, [location.pathname, setGlobalSearchString]);

  const [notificationCount] = useState("");

  const getSearchTooltip = () => {
    if (customSearchTooltip) return customSearchTooltip;

    const path = location.pathname;
    if (path.includes("/logbook-reports")) {
      return "Search by: Shift, Equipment, Machine Name, Mine Id, Mine Name";
    }
    if (path.includes("/main-page")) {
      return "Search by: Equipment Id, Mine Code, Equipment Description, Equipment Category, Equipment Category Description, Equipment Barcode";
    }
    if (path.includes("/assign-equipment")) {
      return "Search by: User Type,	Incharge Name, Designation,	Equipment Details";
    }
    if (path.includes("/equipment-reports")) {
      return "Search by:Equipment Id, Equipment Category,	Mechanic Name,Incharge Name, Approved By";
    }
    if (path.includes("/contractor-users")) {
      return "Search by: User Name, User ID, Designation";
    }
    if (path.includes("/shift-management")) {
      return "Search by: Shift Name, Mine Name";
    }
    if (path.includes("/sap-exports")) {
      return "Search by: 	File Name";
    }
    if (path.includes("/report/attendance")) {
      return "Search by: Gang No, Gangman Name";
    }
    if (path.includes("/settings/employee-master")) {
      return "Search by: Name, Employee No, Position Name, Department, Employee Group";
    }
    if (path.includes("/settings/equipment-master")) {
      return "Search by: Equipment Id, Mine Id, Descriptions, Equipment Description, Equipment Description 2, Equipment Category, LMVS";
    }
    if (path.includes("/settings/gang-master")) {
      return "Search by: Gang No, Employee Name, Department, Personnel Number, Personnel Area, Start Date, End Date, Identity Number, Level, Stope, Allocation";
    }
    if (path.includes("/settings/rate-master")) {
      return "Search by: Job Code, Job Type, Job Description, Start Date, End Date, Unit, Currency";
    }
    return "Search...";
  };

  const getSearchPlaceholder = () => {
    if (customSearchPlaceholder) return customSearchPlaceholder;

    const tooltip = getSearchTooltip();
    if (tooltip === "Search...") return tooltip;

    const prefix = "Search by: ";
    if (tooltip.startsWith(prefix)) {
      const fieldsStr = tooltip.substring(prefix.length);
      const fields = fieldsStr.split(",").map((f) => f.trim());
      if (fields.length <= 4) {
        return `Search by ${fields.join(", ")}...`;
      } else {
        return `Search by ${fields.slice(0, 4).join(", ")}...`;
      }
    }

    return tooltip;
  };

  const placeholderText = getSearchPlaceholder();
  const tooltipText = getSearchTooltip();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setShowProfileDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="mainHeaderWrapper">
      <div style={{ display: "flex", alignItems: "center", flex: 1, gap: "12px" }}>
        <button className="sidebar-toggle-btn" onClick={onMenuClick}>
          <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round">
            <line x1="3" y1="12" x2="21" y2="12"></line>
            <line x1="3" y1="6" x2="21" y2="6"></line>
            <line x1="3" y1="18" x2="21" y2="18"></line>
          </svg>
        </button>
        {location.pathname !== "/panel/dashboard" &&
          location.pathname !== "/panel/notification" &&
          location.pathname !== "/panel/report" &&
          location.pathname !== "/panel/equipment-report-details" && (
            <div className="headerSearch">
              <input type="text" placeholder={placeholderText} title={tooltipText} value={globalSearchString} onChange={handleSearchChange} />
              <span className="searchIcon">{svgData?.searchIcon}</span>
            </div>
          )}
      </div>

      <div className="headerRight">
        <div className="mineStatus">
          <span className="statusDot"></span>
          <span style={{ textTransform: "capitalize" }}>{getUserDetails()?.mineName || "--"}</span>
        </div>

        <div className="notificationIcon" onClick={() => navigate("/panel/notification")}>
          {svgData?.bellIcon}
          {notificationCount && notificationCount !== "0" && <span className="notificationBadge">{notificationCount}</span>}
        </div>

        <div className="profileWrapper" ref={profileRef}>
          <div className="profileIcon" onClick={() => setShowProfileDropdown(!showProfileDropdown)}>
            {svgData?.profileIcon}
          </div>

          <div className={`profileDropdown ${showProfileDropdown ? "profileDropdownOpen" : ""}`}>
            <div className="profileTop">
              <div className="profileAvatar">{getUserDetails()?.name?.charAt(0) || "U"}</div>

              <div>
                <div className="profileName">{getUserDetails()?.name || "--"}</div>

                <div className="profileDesignation">{Array.isArray(getUserDetails()?.designation) ? getUserDetails().designation.join(", ") : getUserDetails()?.designation || "--"}</div>
              </div>
            </div>

            <div className="profileInfoItem">
              <span>Employee Group</span>
              <strong>{getUserDetails()?.employeeGroup || "--"}</strong>
            </div>

            <div className="profileInfoItem">
              <span>Mine ID</span>
              <strong>{getUserDetails()?.mineId || "--"}</strong>
            </div>

            <div className="profileInfoItem">
              <span>Name</span>
              <strong>{getUserDetails()?.name || "--"}</strong>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
